







import { Prop, Component, Vue, Watch, Ref } from 'vue-property-decorator'
import { Dashboard } from '@/api/model/dashboard'
import { DashboardAPI } from '@/api/backend-client'
import { AuthClient } from '@/api/auth-client'
import moment from 'moment'
import * as pbi from 'powerbi-client'

interface EmbedTokenResponse {
    dashboardId: string,
    token: string,
    tokenId: string,
    expiration: string,
}

/** A new token is fetched this long (ms) before the current one expires */
const REFRESH_BEFORE_EXPIRATION = 2 * 60 * 1000

/**
 * The lifetime (ms) a new token is given, counted from when it arrived, between these two. keyvue-api issues tokens
 * for 15 minutes (EmbedTokenService, lifetimeInMinutes), but its expiration is by its own clock: here it looks
 * shorter when this clock runs ahead (going by it would refresh almost at once) and longer when it runs behind (the
 * refresh would come after the token expired). An expiration that can't be read gets the minimum. The minimum also
 * keeps the refresh at least MINIMUM_TOKEN_LIFETIME - REFRESH_BEFORE_EXPIRATION after a new token.
 */
const MINIMUM_TOKEN_LIFETIME = 5 * 60 * 1000
const MAXIMUM_TOKEN_LIFETIME = 15 * 60 * 1000

/** The waits (ms) before the next try when fetching a new token failed; then the last one again */
const REFRESH_RETRY_DELAYS = [5, 10, 20, 40, 60].map(seconds => seconds * 1000)

@Component
export default class PowerBiReport extends Vue {
    @Prop({ required: true }) readonly dashboard!: Dashboard
    @Ref() readonly container!: HTMLDivElement
    embedToken: EmbedTokenResponse = null
    /** When the current token expires by this clock (ms), see MINIMUM_TOKEN_LIFETIME */
    embedTokenExpiresAt: number = null
    embedTokenRefreshTimeout: ReturnType<typeof setTimeout> = null
    embedTokenRefreshAttempts: number = 0
    errorMessage: string = null
    active: boolean = true

    powerbi = new pbi.service.Service(
        pbi.factories.hpmFactory,
        pbi.factories.wpmpFactory,
        pbi.factories.routerFactory,
    )

    async mounted(): Promise<void> {
        this.powerbi.bootstrap(this.container, {
            type: 'report',
            settings: {
                localeSettings: {
                    language: this.$i18n.locale,
                },
                bars: {
                    actionBar: { visible: true },
                    statusBar: { visible: true },
                },
            },
        })

        try {
            const embedToken = await this.requestEmbedToken()
            if (!this.active) {
                return
            }

            this.setEmbedToken(embedToken)

        } catch (error) {
            // A 403 (e.g. a Power BI role that is no longer valid), a 404, ...: the server's message instead of
            // the report, which would otherwise keep loading
            if (this.active) {
                await this.showError(error)
            }

            return
        }

        await this.createDashboard()
    }

    async beforeDestroy(): Promise<void> {
        this.active = false
        this.unscheduleEmbedTokenRefresh()
        await this.destroyDashboard()
    }

    async requestEmbedToken(): Promise<EmbedTokenResponse> {
        const client = new AuthClient().axiosClient
        const basePath = process.env.VUE_APP_KEYVUE_BACKEND_BASEPATH || ''
        const response = await client.post(`${basePath}/service/power_bi/embed_token`, {
            dashboardId: this.dashboard.id,
        })

        return response.data
    }

    /**
     * A new token before the current one expires. When that fails it is tried again, after 5, 10, 20, 40 and then
     * every 60 seconds, until it works: a token that expires meanwhile shows Power BI's own message in the report,
     * and the next token brings it back. A 401 (the session has ended), 403 or 404 shows the server's message
     * instead of the report: this clock alone (it may run ahead) never takes a working report away.
     */
    async refreshEmbedToken(): Promise<void> {
        this.embedTokenRefreshTimeout = null

        try {
            const embedToken = await this.requestEmbedToken()
            if (!this.active) {
                return
            }

            this.embedTokenRefreshAttempts = 0
            this.setEmbedToken(embedToken)

        } catch (error) {
            if (!this.active) {
                return
            }

            const status = error?.response?.status
            if (status === 401 || status === 403 || status === 404 || !new AuthClient().isAuthenticated) {
                await this.showError(error)

                return
            }

            const delay = REFRESH_RETRY_DELAYS[Math.min(this.embedTokenRefreshAttempts, REFRESH_RETRY_DELAYS.length - 1)]

            this.embedTokenRefreshAttempts++
            this.embedTokenRefreshTimeout = setTimeout(this.refreshEmbedToken, delay)
        }
    }

    /** The new token, with when it expires by this clock (see MINIMUM_TOKEN_LIFETIME) */
    setEmbedToken(embedToken: EmbedTokenResponse): void {
        const receivedAt = Date.now()
        const lifetime = moment(embedToken.expiration).diff(moment(receivedAt))
        const bounded = Number.isFinite(lifetime)
            ? Math.min(Math.max(lifetime, MINIMUM_TOKEN_LIFETIME), MAXIMUM_TOKEN_LIFETIME)
            : MINIMUM_TOKEN_LIFETIME

        this.embedToken = embedToken
        this.embedTokenExpiresAt = receivedAt + bounded
    }

    timeUntilExpiration(): number {
        return this.embedTokenExpiresAt - Date.now()
    }

    async showError(error: any): Promise<void> {
        this.unscheduleEmbedTokenRefresh()
        await this.destroyDashboard()
        this.errorMessage = this.getErrorMessage(error)
    }

    getErrorMessage(error: any): string {
        // API Platform's error, as JSON-LD or as problem+json
        const data = error?.response?.data
        if (data?.['hydra:description'] || data?.detail) {
            return data['hydra:description'] || data.detail
        }

        // No response: AuthClient's "no connection" message
        if (!error?.response && error?.message) {
            return error.message
        }

        return this.$t('error.text').toString()
    }

    @Watch('embedToken.token')
    updateEmbedToken(newToken: string|null, oldToken: string|null): void {
        if (!oldToken) {
            return
        }

        const report = this.powerbi.get(this.container)
        report.setAccessToken(this.embedToken.token)
    }

    @Watch('embedTokenExpiresAt')
    scheduleEmbedTokenRefresh(): void {
        this.unscheduleEmbedTokenRefresh()

        if (!this.active) {
            return
        }

        this.embedTokenRefreshTimeout = setTimeout(
            this.refreshEmbedToken,
            this.timeUntilExpiration() - REFRESH_BEFORE_EXPIRATION,
        )
    }

    unscheduleEmbedTokenRefresh(): void {
        if (this.embedTokenRefreshTimeout) {
            clearTimeout(this.embedTokenRefreshTimeout)
            this.embedTokenRefreshTimeout = null
        }
    }

    async createDashboard(): Promise<void> {
        this.powerbi.embed(this.container, {
            type: 'report',
            id: this.dashboard.powerBiId,
            tokenType: pbi.models.TokenType.Embed,
            accessToken: this.embedToken.token,
        })
    }

    async destroyDashboard(): Promise<void> {
        // Gone once the error message is shown
        if (this.container) {
            this.powerbi.reset(this.container)
        }
    }
}
